import React, { useState, useEffect } from "react";
import Content from "../components/Content";
import Layout from "../containers/LayoutAdministrativo";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EgresosIngresosService from "../services/EgresosIngresosService";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Paper,
  Typography,
  OutlinedInput,
  Stack,
  Box,
  IconButton,
  Button,
  Tooltip,
  tooltipClasses,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import Swal from "sweetalert2";

const EgresosIngresos = () => {
  useEffect(() => {
    document.title = "Egresos e Ingresos";
  }, []);

  function mostrarMensaje(mensaje) {
    Swal.fire({
      title: "",
      text: mensaje,
      icon: "success",
      confirmButtonText: "Aceptar",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.reload();
      }
    });
  }

  function mostrarMensajeEI(mensaje) {
    Swal.fire({
      title: "",
      text: mensaje,
      icon: "success",
      confirmButtonText: "Aceptar",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.close();
      }
    });
  }

  function mostrarError(mensaje) {
    Swal.fire({
      title: "¡Error!",
      text: mensaje,
      icon: "error",
      confirmButtonText: "Aceptar",
    });
  }

  function reloadPage() {
    window.location.reload();
  }

  const [editMode, setEditMode] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [selectedGroupFecha, setSelectedGroupFecha] = useState("");
  const [egresosIngresos, setEgresosIngresos] = useState([
    { concepto: "", cantidad: "", formaPago: "efectivo", operacion: "egreso" },
  ]);
  const [conceptoModificado, setConceptoModificado] = useState({});

  const addEgresoIngreso = () => {
    setEgresosIngresos((prev) => [
      ...prev,
      {
        concepto: "",
        cantidad: "",
        formaPago: "efectivo",
        operacion: "egreso",
      },
    ]);
  };

  const handleChangeEgresosIngresos = (index, field, value) => {
    const nuevosEgresosIngresos = [...egresosIngresos];
    nuevosEgresosIngresos[index] = {
      ...nuevosEgresosIngresos[index],
      [field]: value,
    };
    setEgresosIngresos(nuevosEgresosIngresos);

    if (field === "concepto") {
      setConceptoModificado((prev) => ({
        ...prev,
        [index]: true,
      }));
    }
  };

  const getDateKey = (fecha) => {
    if (!fecha) return "";
    return fecha.includes(" ") ? fecha.split(" ")[0] : fecha;
  };

  const formatDateDisplay = (fecha) => {
    if (!fecha) return "";

    const texto = String(fecha).trim();
    const [fechaParte] = texto.split(" ");
    const match = fechaParte?.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);

    if (match) {
      const yyyy = match[1];
      const mm = String(match[2]).padStart(2, "0");
      const dd = String(match[3]).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    return texto;
  };

  const formatDateTimeDisplay = (fecha) => {
    if (!fecha) return "";

    const texto = String(fecha).trim();
    const [fechaParte, horaParte] = texto.split(" ");
    const match = fechaParte?.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);

    if (match) {
      const yyyy = match[1];
      const mm = String(match[2]).padStart(2, "0");
      const dd = String(match[3]).padStart(2, "0");

      if (horaParte) {
        const timeMatch = horaParte.match(/(\d{1,2}):(\d{2})/);
        if (timeMatch) {
          const hh = String(timeMatch[1]).padStart(2, "0");
          const min = String(timeMatch[2]).padStart(2, "0");
          return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
        }
      }

      return `${yyyy}-${mm}-${dd}`;
    }

    const parsed = new Date(texto);
    if (!Number.isNaN(parsed.getTime())) {
      return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")} ${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`;
    }

    return texto;
  };

  const parseCantidad = (value) => {
    const numero = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
    return Number.isFinite(numero) ? numero : 0;
  };

  const formatCurrency = (value) =>
    `$${Number(value || 0).toLocaleString("es-MX", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const totalesActuales = egresosIngresos.reduce(
    (acc, item) => {
      const cantidad = parseCantidad(item?.cantidad);
      if (item?.operacion === "egreso") {
        acc.egresos += cantidad;
      }
      if (item?.operacion === "ingreso") {
        acc.ingresos += cantidad;
      }
      return acc;
    },
    { egresos: 0, ingresos: 0 },
  );

  const groupEgresosIngresosByFechaSucursal = (items) => {
    const groups = {};
    items.forEach((item) => {
      const fecha = getDateKey(item.fecha);
      const fechaCompleta = item.fecha || fecha;
      const sucursal = item.nombreSucursal || item.sucursal || "";
      const key = `${fecha}|${sucursal}`;
      if (!groups[key]) {
        groups[key] = {
          fecha,
          fechaCompleta,
          sucursal,
          items: [],
        };
      }
      groups[key].items.push(item);
    });
    return Object.values(groups);
  };

  const verEgresosIngresosPorGrupo = (grupo, editable = false) => {
    setEgresosIngresos(grupo.items.map((item) => ({ ...item })));
    setSelectedGroupFecha(grupo.fechaCompleta || grupo.fecha);
    setEditMode(editable);
    setViewOnly(!editable);
  };

  const normalizeId = (item) => {
    const rawId =
      item?.idEgresoIngreso ??
      item?.idEgreso ??
      item?.id ??
      item?.idRegistro ??
      item?.idEgresoIngresoId ??
      item?._id;
    return rawId == null ? "" : String(rawId);
  };

  const getCurrentCorteInfo = async (sucursalId) => {
    try {
      const response = await EgresosIngresosService.getEgresosIngresosActual(
        sucursalId,
      );
      const data = response?.data ?? {};
      const items = [
        ...(Array.isArray(data?.egresosIngresos) ? data.egresosIngresos : []),
        ...(Array.isArray(data?.ingresos) ? data.ingresos : []),
        ...(Array.isArray(data?.egresos) ? data.egresos : []),
        ...(Array.isArray(data) ? data : []),
      ];
      const ids = new Set(items.map((item) => normalizeId(item)));
      const groupKeys = new Set(
        items.map((item) => {
          const fecha = getDateKey(item?.fecha);
          const sucursal = item?.nombreSucursal || item?.sucursal || "";
          return `${fecha}|${sucursal}`;
        }),
      );
      return { ids, groupKeys };
    } catch (error) {
      console.error("Error al obtener el corte actual:", error);
      return { ids: new Set(), groupKeys: new Set() };
    }
  };

  async function mostrarBuscarEgresosIngresos() {
    let EIOriginal = [];
    let gruposActivos = [];
    let gruposActivosByKey = {};

    const updateGroupMap = (grupos) => {
      gruposActivosByKey = {};
      grupos.forEach((grupo) => {
        gruposActivosByKey[`${grupo.fecha}|${grupo.sucursal}`] = grupo;
      });
    };

    const cargarGrupos = (items) => {
      const grupos = groupEgresosIngresosByFechaSucursal(items || []);
      updateGroupMap(grupos);
      return grupos;
    };

    const resultado = await EgresosIngresosService.getEgresosIngresos();
    EIOriginal = resultado.data.egresosIngresos || [];
    gruposActivos = cargarGrupos(EIOriginal);
    const currentUser = JSON.parse(sessionStorage.getItem("usuario") || "{}");
    const currentCorteInfo = await getCurrentCorteInfo(currentUser?.sucursal);
    const isGrupoEditable = (grupo) => {
      const groupKey = `${getDateKey(grupo.fecha)}|${grupo.sucursal || ""}`;
      const hasMatchingId = grupo.items.some((item) =>
        currentCorteInfo.ids.has(normalizeId(item)),
      );
      const hasMatchingGroup = currentCorteInfo.groupKeys.has(groupKey);
      return hasMatchingId || hasMatchingGroup;
    };

    const porPagina = 8;
    let paginaActual = 1;

    const construirFilas = (grupos) => {
      return grupos
        .map(
          (grupo, idx) => {
            const editable = isGrupoEditable(grupo);
            return `
        <tr style="background-color: ${idx % 2 === 0 ? "#ffffff" : "#f8fafc"}; border-bottom: 1px solid #e2e8f0; font-size: 0.80rem; transition: background 0.15s;">
          <td style="padding: 6px 12px; text-align: left; font-weight: 500; color: #1e293b; border-right: 1px solid #f1f5f9;">${grupo.sucursal}</td>
          <td style="padding: 6px 10px; text-align: center; color: #475569; font-size: 0.78rem; white-space: nowrap; border-right: 1px solid #f1f5f9;">${formatDateDisplay(grupo.fecha)}</td>
          <td style="padding: 4px 6px; text-align: center; border-right: 1px solid #f1f5f9;">
            <button data-action="mostrar" data-group-key="${grupo.fecha}|${grupo.sucursal}" title="Mostrar" style="background: #e0f2fe; color: #0284c7; border: none; border-radius: 5px; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 0.85rem;">
              <i class="fas fa-eye"></i>
            </button>
          </td>
          <td style="padding: 4px 6px; text-align: center; border-right: 1px solid #f1f5f9;">
            <button data-action="editar" data-group-key="${grupo.fecha}|${grupo.sucursal}" ${editable ? "" : "disabled"} title="${editable ? "Editar" : "Solo editable en corte actual"}" style="background: ${editable ? "#fef3c7" : "#f1f5f9"}; color: ${editable ? "#d97706" : "#94a3b8"}; border: none; border-radius: 5px; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: ${editable ? "pointer" : "not-allowed"}; transition: all 0.2s; font-size: 0.85rem;">
              <i class="fas fa-edit"></i>
            </button>
          </td>
          <td style="padding: 4px 6px; text-align: center;">
            <button data-action="eliminar" data-group-key="${grupo.fecha}|${grupo.sucursal}" ${editable ? "" : "disabled"} title="${editable ? "Eliminar" : "Solo eliminable en corte actual"}" style="background: ${editable ? "#fee2e2" : "#f1f5f9"}; color: ${editable ? "#dc2626" : "#94a3b8"}; border: none; border-radius: 5px; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: ${editable ? "pointer" : "not-allowed"}; transition: all 0.2s; font-size: 0.85rem;">
              <i class="fas fa-trash"></i>
            </button>
          </td>
        </tr>
      `;
          }
        )
        .join("");
    };

    const eliminarGrupo = async (grupo) => {
      if (!isGrupoEditable(grupo)) {
        mostrarError("Solo se pueden eliminar operaciones del corte actual.");
        return;
      }

      try {
        for (const item of grupo.items) {
          if (item.idEgresoIngreso) {
            await EgresosIngresosService.eliminarEgresosIngresos(
              item.idEgresoIngreso,
            );
          }
        }
        const resultado = await EgresosIngresosService.getEgresosIngresos();
        EIOriginal = resultado.data.egresosIngresos || [];
        gruposActivos = cargarGrupos(EIOriginal);
        paginaActual = 1;
        renderPagina(paginaActual);
        mostrarMensajeEI("Operaciones eliminadas con éxito.");
      } catch (error) {
        console.error("Error al eliminar grupo:", error);
        mostrarError("No se pudo eliminar el grupo de operaciones.");
      }
    };

    const renderPagina = (pagina) => {
      const inicio = (pagina - 1) * porPagina;
      const fin = inicio + porPagina;
      const paginaGrupos = gruposActivos.slice(inicio, fin);
      const tabla = document.getElementById("tablaEgresosIngresos");
      if (tabla) {
        if (!paginaGrupos || paginaGrupos.length === 0) {
          tabla.innerHTML = `<tr><td colspan="5" style="padding: 24px; text-align: center; color: #64748b; font-style: italic;">No hay registros</td></tr>`;
        } else {
          tabla.innerHTML = construirFilas(paginaGrupos);
        }

        tabla
          .querySelectorAll("button[data-action='mostrar']")
          .forEach((btn) => {
            btn.addEventListener("click", () => {
              const key = btn.dataset.groupKey;
              const grupo = gruposActivosByKey[key];
              if (grupo) {
                verEgresosIngresosPorGrupo(grupo, false);
                Swal.close();
              }
            });
          });
        tabla
          .querySelectorAll("button[data-action='editar']")
          .forEach((btn) => {
            btn.addEventListener("click", () => {
              const key = btn.dataset.groupKey;
              const grupo = gruposActivosByKey[key];
              if (grupo) {
                if (!isGrupoEditable(grupo)) {
                  verEgresosIngresosPorGrupo(grupo, false);
                  mostrarError("Solo se pueden editar operaciones del corte actual.");
                  Swal.close();
                  return;
                }
                verEgresosIngresosPorGrupo(grupo, true);
                Swal.close();
              }
            });
          });
        tabla
          .querySelectorAll("button[data-action='eliminar']")
          .forEach((btn) => {
            btn.addEventListener("click", () => {
              const key = btn.dataset.groupKey;
              const grupo = gruposActivosByKey[key];
              if (grupo) {
                if (!isGrupoEditable(grupo)) {
                  mostrarError("Solo se pueden eliminar operaciones del corte actual.");
                  return;
                }
                Swal.fire({
                  title: "¿Eliminar operaciones?",
                  text: `Se eliminarán todos los movimientos de esa fecha y sucursal.`,
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Sí, eliminar",
                  cancelButtonText: "Cancelar",
                }).then(async (result) => {
                  if (result.isConfirmed) {
                    await eliminarGrupo(grupo);
                  }
                });
              }
            });
          });
      }

      const totalPaginas = Math.max(
        1,
        Math.ceil(gruposActivos.length / porPagina),
      );
      const spanPagina = document.getElementById("paginaActual");
      if (spanPagina) {
        spanPagina.textContent = `Página ${pagina} de ${totalPaginas} (${gruposActivos.length} registros)`;
      }
      const btnAnterior = document.getElementById("btnAnterior");
      const btnSiguiente = document.getElementById("btnSiguiente");
      if (btnAnterior) {
        btnAnterior.disabled = pagina <= 1;
        btnAnterior.style.opacity = pagina <= 1 ? "0.45" : "1";
        btnAnterior.style.cursor = pagina <= 1 ? "not-allowed" : "pointer";
      }
      if (btnSiguiente) {
        btnSiguiente.disabled = pagina >= totalPaginas;
        btnSiguiente.style.opacity = pagina >= totalPaginas ? "0.45" : "1";
        btnSiguiente.style.cursor = pagina >= totalPaginas ? "not-allowed" : "pointer";
      }
    };
    Swal.fire({
      title: "BUSCAR OPERACIONES",
      width: "65%",
      customClass: {
        title: "tituloDegradado",
        popup: "swalMaxHeight",
      },
      html: `
        <div style="font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; text-align: left;">
          <div style="display: flex; gap: 10px; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; margin-bottom: 8px;">
            <div style="display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 220px;">
              <label for="buscarEgresoIngreso" style="font-size: 0.80rem; font-weight: 600; color: #334155;">Fecha de la operación:</label>
              <input id="buscarEgresoIngreso" type="date" style="padding: 5px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.82rem; outline: none; height: 32px; box-sizing: border-box;" placeholder="" />
            </div>
            <div>
              <button id="btnMostrarTodos" style="background: linear-gradient(180deg, #0f4b75 0%, #006787 100%); color: white; border: none; border-radius: 6px; font-weight: 600; font-size: 0.80rem; padding: 6px 14px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.1); height: 32px; white-space: nowrap; transition: opacity 0.2s;">
                Mostrar todas las operaciones
              </button>
            </div>
          </div>
  
          <div style="width: 100%; overflow: auto; max-height: 52vh; border: 1px solid #cbd5e1; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.08); background: white;">
            <table class="tablaEgresosIngresos" style="width: 100%; border-collapse: collapse; text-align: center;">
              <thead>
                <tr style="background: linear-gradient(180deg, #0f4b75 0%, #006787 100%); color: white; font-size: 0.80rem; position: sticky; top: 0; z-index: 2;">
                  <th style="padding: 7px 12px; text-align: left; border-right: 1px solid rgba(255,255,255,0.15); font-weight: 600;">Sucursal</th>
                  <th style="padding: 7px 10px; text-align: center; border-right: 1px solid rgba(255,255,255,0.15); font-weight: 600;">Fecha</th>
                  <th style="padding: 7px 8px; text-align: center; border-right: 1px solid rgba(255,255,255,0.15); font-weight: 600; width: 45px;">Mostrar</th>
                  <th style="padding: 7px 8px; text-align: center; border-right: 1px solid rgba(255,255,255,0.15); font-weight: 600; width: 45px;">Editar</th>
                  <th style="padding: 7px 8px; text-align: center; font-weight: 600; width: 45px;">Eliminar</th>
                </tr>
              </thead>
              <tbody id="tablaEgresosIngresos">
              </tbody>
            </table>
          </div>

          <div style="margin-top: 8px; display: flex; justify-content: space-between; align-items: center;">
            <span id="paginaActual" style="font-size: 0.80rem; color: #64748b; font-weight: 500;"></span>
            <div style="display: flex; gap: 6px;">
              <button id="btnAnterior" style="padding: 4px 12px; background: #006787; color: white; border: none; border-radius: 6px; font-size: 0.80rem; font-weight: 600; cursor: pointer; transition: opacity 0.2s;">Anterior</button>
              <button id="btnSiguiente" style="padding: 4px 12px; background: #006787; color: white; border: none; border-radius: 6px; font-size: 0.80rem; font-weight: 600; cursor: pointer; transition: opacity 0.2s;">Siguiente</button>
            </div>
          </div>
        </div>
      `,
      showConfirmButton: false,
      didOpen: () => {
        renderPagina(paginaActual);

        document.getElementById("btnAnterior").addEventListener("click", () => {
          if (paginaActual > 1) {
            paginaActual--;
            renderPagina(paginaActual);
          }
        });

        document
          .getElementById("btnSiguiente")
          .addEventListener("click", () => {
            if (paginaActual < Math.ceil(gruposActivos.length / porPagina)) {
              paginaActual++;
              renderPagina(paginaActual);
            }
          });

        document
          .getElementById("btnMostrarTodos")
          .addEventListener("click", async () => {
            try {
              const resultado = await EgresosIngresosService.getEgresosIngresos();
              EIOriginal = resultado.data.egresosIngresos || [];
              gruposActivos = cargarGrupos(EIOriginal);
              paginaActual = 1;
              renderPagina(paginaActual);
            } catch (error) {
              console.error("Error al recargar egresos/ingresos:", error);
            }
            document.getElementById("buscarEgresoIngreso").value = "";
          });
        document
          .getElementById("buscarEgresoIngreso")
          .addEventListener("input", async (e) => {
            const valor = e.target.value.trim();
            paginaActual = 1;
            if (valor === "") {
              gruposActivos = cargarGrupos(EIOriginal);
              renderPagina(paginaActual);
              return;
            }

            try {
              const resultado = await EgresosIngresosService.buscarEgresosIngresos(valor);
              const resultados = resultado.egresosIngresos || [];
              gruposActivos = cargarGrupos(resultados);
              renderPagina(paginaActual);
            } catch (error) {
              console.error("Error en búsqueda:", error);
            }
          });
      },
    });
  }



  const isEgresoIngresoEmpty = (egresoIngreso) => {
    const concepto = egresoIngreso.concepto ?? "";
    const cantidad = egresoIngreso.cantidad ?? "";
    return (
      concepto.toString().trim() === "" &&
      cantidad.toString().trim() === ""
    );
  };

  const removeAllEmpty = (arr) => arr.filter((s) => !isEgresoIngresoEmpty(s));
  const tieneUnoConDatos = (arr) => arr.some((s) => !isEgresoIngresoEmpty(s));

  const agregarEgresosIngresos = async () => {
    let egresosIngresosIns;
    let egresosIngresosUpd;
    const cleaned = removeAllEmpty(egresosIngresos);
    if (!tieneUnoConDatos(cleaned)) {
      mostrarError("Es necesario al menos un egreso o ingreso.");
      return;
    }
    egresosIngresosIns = {
      fecha: getFormattedNow(),
      empleado: currentU.id,
      sucursal: currentU.sucursal,
      egresosIngresos: cleaned,
    };
    egresosIngresosUpd = {
      fecha: getFormattedNow(),
      empleado: currentU.id,
      sucursal: currentU.sucursal,
      egresosIngresos: cleaned,
    };

    try {
      let resultado;

      if (editMode) {
        resultado =
          await EgresosIngresosService.updateEgresosIngresos(egresosIngresosUpd);
        mostrarMensajeEI("Movimientos actualizados con éxito!");
      } else {
        resultado =
          await EgresosIngresosService.agregarEgresosIngresos(egresosIngresosIns);
        if (resultado.data.estatus === "OK") {
          mostrarMensajeEI("Movimientos agregados con éxito!");
        } else {
          mostrarError(
            "No se pudieron agregar los movimientos: " + resultado.data.mensaje,
          );
        }
      }
    } catch (error) {
      mostrarError("Error al actualizar los movimientos:", error);
    }
  };

  const handleBlurConcepto = (index, value) => {
    const conceptoSinEspacios = value?.trim() ?? "";
    const fueModificado = conceptoModificado[index];
    const esUltimaFila = index === egresosIngresos.length - 1;

    if (
      !viewOnly &&
      fueModificado &&
      esUltimaFila &&
      conceptoSinEspacios !== ""
    ) {
      addEgresoIngreso();
    }

    setConceptoModificado((prev) => ({
      ...prev,
      [index]: false,
    }));
  };

  const handleRemoveEgresoIngreso = (index) => {
    setEgresosIngresos(egresosIngresos.filter((_, i) => i !== index));
  };

  function getFormattedNow() {
    const now = new Date();

    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd} ${hh}:${minutes}`;
  }

  const usuarioString = sessionStorage.getItem("usuario");
  const currentU = JSON.parse(usuarioString);

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "50%",
            paddingTop: "2vh",
            paddingBottom: "2vh",
          }}
        >
          <Stack spacing={2} sx={{ width: "95vw", maxWidth: "96vw" }}>
            <Paper
              elevation={3}
              sx={{ p: 2, borderRadius: 4 }}
              className="paperSol"
            >
              <Grid item xs={12}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h9"
                    gutterBottom
                    sx={{
                      background: "linear-gradient(90deg, #006787, #014e65)",
                      color: "white",
                      padding: "0.5vw 1vw",
                      borderRadius: "8px",
                      display: "inline-block",
                      whiteSpace: "pre-line",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: "bold", justifyContent: "center" }}
                    >
                      EGRESOS/INGRESOS
                    </Box>
                    <br />
                    <Box component="span" sx={{ fontWeight: "normal" }}>
                      Correspondientes a la Sucursal: {currentU.nombreSucursal}
                    </Box>
                  </Typography>
                </Box>
              </Grid>

              {/* Contenedor principal */}
              <Grid container spacing={"0.3vw"}>
                <Grid
                  item
                  xs={12}
                  container
                  spacing={"1vw"}
                  sx={{
                    paddingTop: "0.6vw",
                    width: "100%",
                    display: "flex",
                  }}
                >
                  <Stack direction="row" spacing={"3vw"}>
                    <Grid item xs={2}>
                      <Table sx={{ width: "100%", backgroundColor: "white", "& .MuiTableCell-root": { padding: "4px 8px" } }}>
                        {" "}
                        <TableHead sx={{ backgroundColor: "#18234f" }}>
                          <TableRow>
                            <TableCell
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                borderBottom: "none",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              Fecha
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                borderBottom: "none",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              Empleado
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                borderBottom: "none",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              Concepto
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                borderBottom: "none",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              Cantidad
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                borderBottom: "none",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              Forma de pago
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                borderBottom: "none",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              Operacion
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                                borderBottom: "none",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              Eliminar
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody
                          sx={{
                            "& .MuiTableCell-root": { padding: "4px 6px" },
                          }}
                        >
                          {egresosIngresos.length > 0 ? (
                            egresosIngresos.map((egresoIngreso, index) => (
                              <React.Fragment key={index}>
                                <TableRow>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <TextField
                                        value={formatDateTimeDisplay(selectedGroupFecha || getFormattedNow())}
                                        disabled={true}
                                        sx={{
                                          width: "10vw",
                                          "& .MuiInputBase-root": {
                                            height: "5vh",
                                          },
                                          "& .MuiInputBase-input": {
                                            fontSize: "1vw",
                                          },
                                        }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Typography
                                        variant="h9"
                                        gutterBottom
                                        sx={{
                                          fontWeight: "bold",
                                          color: "black",
                                          padding: "0.5vw 1vw",
                                          borderRadius: "8px",
                                          display: "inline-block",

                                          textAlign: "center",
                                        }}
                                      >
                                        {currentU.nombre}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <TextField
                                        value={egresoIngreso.concepto}
                                        onChange={(e) =>
                                          handleChangeEgresosIngresos(
                                            index,
                                            "concepto",
                                            e.target.value,
                                          )
                                        }
                                        onBlur={(e) =>
                                          handleBlurConcepto(
                                            index,
                                            e.target.value,
                                          )
                                        }
                                        disabled={viewOnly}
                                        sx={{
                                          width: "20vw",
                                          "& .MuiInputBase-root": {
                                            height: "5vh",
                                          },
                                          "& .MuiInputBase-input": {
                                            fontSize: "1vw",
                                          },
                                        }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <TextField
                                        value={egresoIngreso.cantidad}
                                        onChange={(e) =>
                                          handleChangeEgresosIngresos(
                                            index,
                                            "cantidad",
                                            e.target.value,
                                          )
                                        }
                                        disabled={viewOnly}
                                        sx={{
                                          width: "6vw",
                                          "& .MuiInputBase-root": {
                                            height: "5vh",
                                          },
                                          "& .MuiInputBase-input": {
                                            fontSize: "1vw",
                                          },
                                        }}
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Select
                                        input={<OutlinedInput />}
                                        value={egresoIngreso.formaPago}
                                        onChange={(e) =>
                                          handleChangeEgresosIngresos(
                                            index,
                                            "formaPago",
                                            e.target.value,
                                          )
                                        }
                                        disabled={viewOnly}
                                        sx={{
                                          flex: "0 0 8vw",
                                          width: "12.51vw",
                                          maxWidth: "100%",

                                          height: "5vh",
                                          minHeight: "0px !important",
                                          overflow: "hidden",

                                          "&.MuiInputBase-root": {
                                            minHeight: "0px !important",
                                          },

                                          "& .MuiSelect-select": {
                                            minHeight: "0px !important",
                                            padding: "0 0.5vw !important",
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: "1vw",
                                            lineHeight: "1",
                                          },

                                          "& .MuiOutlinedInput-notchedOutline":
                                          {
                                            top: 0,
                                            "& legend": { display: "none" },
                                          },

                                          "& .MuiSvgIcon-root": {
                                            display: "inline-block !important",
                                            position: "absolute",
                                            right: "0.5vw",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            fontSize: "1.2vw !important",
                                            width: "1.2vw !important",
                                            height: "1.2vw !important",
                                            pointerEvents: "none",
                                          },
                                          minWidth: "0px !important",
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                        }}
                                        className="selectFormaPago"
                                      >
                                        <MenuItem
                                          sx={{
                                            fontSize: "1vw",
                                            padding: "0.5vw",
                                          }}
                                          value="seleccione"
                                          disabled
                                        >
                                          Seleccione
                                        </MenuItem>
                                        <MenuItem
                                          sx={{
                                            fontSize: "1vw",
                                            padding: "0.5vw",
                                          }}
                                          value="efectivo"
                                        >
                                          Efectivo
                                        </MenuItem>
                                        <MenuItem
                                          sx={{
                                            fontSize: "1vw",
                                            padding: "0.5vw",
                                          }}
                                          value="tarjeta"
                                        >
                                          Tarjeta
                                        </MenuItem>
                                        <MenuItem
                                          sx={{
                                            fontSize: "1vw",
                                            padding: "0.5vw",
                                          }}
                                          value="cheque"
                                        >
                                          Cheque
                                        </MenuItem>
                                        <MenuItem
                                          sx={{
                                            fontSize: "1vw",
                                            padding: "0.5vw",
                                          }}
                                          value="transferencia"
                                        >
                                          Transferencia
                                        </MenuItem>
                                      </Select>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Select
                                        input={<OutlinedInput />}
                                        value={egresoIngreso.operacion}
                                        onChange={(e) =>
                                          handleChangeEgresosIngresos(
                                            index,
                                            "operacion",
                                            e.target.value,
                                          )
                                        }
                                        disabled={viewOnly}
                                        sx={{
                                          flex: "0 0 8vw",
                                          width: "12.51vw",
                                          maxWidth: "100%",

                                          height: "5vh",
                                          minHeight: "0px !important",
                                          overflow: "hidden",

                                          "&.MuiInputBase-root": {
                                            minHeight: "0px !important",
                                          },

                                          "& .MuiSelect-select": {
                                            minHeight: "0px !important",
                                            padding: "0 0.5vw !important",
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: "1vw",
                                            lineHeight: "1",
                                          },

                                          "& .MuiOutlinedInput-notchedOutline":
                                          {
                                            top: 0,
                                            "& legend": { display: "none" },
                                          },

                                          "& .MuiSvgIcon-root": {
                                            display: "inline-block !important",
                                            position: "absolute",
                                            right: "0.5vw",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            fontSize: "1.2vw !important",
                                            width: "1.2vw !important",
                                            height: "1.2vw !important",
                                            pointerEvents: "none",
                                          },
                                          minWidth: "0px !important",
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                        }}
                                        className="selectOperacion"
                                      >
                                        <MenuItem
                                          sx={{
                                            fontSize: "1vw",
                                            padding: "0.5vw",
                                          }}
                                          value="seleccione"
                                          disabled
                                        >
                                          Seleccione
                                        </MenuItem>
                                        <MenuItem
                                          sx={{
                                            fontSize: "1vw",
                                            padding: "0.5vw",
                                          }}
                                          value="egreso"
                                        >
                                          Egreso
                                        </MenuItem>
                                        <MenuItem
                                          sx={{
                                            fontSize: "1vw",
                                            padding: "0.5vw",
                                          }}
                                          value="ingreso"
                                        >
                                          Ingreso
                                        </MenuItem>
                                      </Select>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      size="large"
                                      sx={{ color: "red" }}
                                      onClick={() =>
                                        handleRemoveEgresoIngreso(index)
                                      }
                                    >
                                      <DeleteIcon sx={{ fontSize: "1.8vw" }} />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                align="center"
                                sx={{ py: 4 }}
                              >
                                No hay registros
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Grid>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  container
                  spacing={"1vw"}
                  sx={{
                    paddingTop: "0.6vw",
                    justifyContent: "flex-end",
                    width: "100%",
                    display: "flex",
                  }}
                >
                  <Stack direction="row" spacing={"3vw"}>
                    <Grid item xs={2}>
                      {" "}
                      <Table sx={{ backgroundColor: "white", "& .MuiTableCell-root": { padding: "4px 8px" } }}>
                        {" "}
                        <TableBody>
                          <TableRow></TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#18234f",
                                color: "white",
                                fontWeight: "bold",
                                width: "8vw",
                              }}
                            >
                              Total de egresos
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                width: "8vw",
                              }}
                            >
                              {formatCurrency(totalesActuales.egresos)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#18234f",
                                color: "white",
                                fontWeight: "bold",
                                width: "8vw",
                              }}
                            >
                              Total de ingresos
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                width: "8vw",
                              }}
                            >
                              {formatCurrency(totalesActuales.ingresos)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </Box>
        <Box
          sx={{
            position: "fixed",
            right: "6vw",
            top: "30%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {!viewOnly && (
            <Tooltip
              title={"Guardar gastos"}
              arrow
              placement="right"
              slotProps={{
                popper: {
                  sx: {
                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                    {
                      marginTop: "0px",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                    {
                      marginBottom: "0.2vw",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                    {
                      marginLeft: "0px",
                    },
                    [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                    {
                      marginRight: "0px",
                    },
                  },
                },
                tooltip: {
                  sx: {
                    fontSize: "0.7vw",
                    padding: "0.5vw 0.5vw",
                    borderRadius: "8px",
                    backgroundColor: "rgba(255,255,255,0.9)",
                    fontWeight: "bold",
                    fontFamily: "Roboto-Medium",
                    color: "#000000",
                    marginBottom: "-5px",
                  },
                },
                arrow: {
                  sx: {
                    color: "rgba(255,255,255,0.9)",
                  },
                },
              }}
            >
              <IconButton
                onClick={agregarEgresosIngresos}
                sx={{
                  backgroundColor: "#ffffff",
                  color: "#18234f",
                  "&:hover": { backgroundColor: "#d1d1d1" },
                  width: "4vw",
                  height: "4vw",
                }}
              >
                <SaveIcon sx={{ fontSize: "1.8vw" }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={"Nuevas operaciones"}
            arrow
            placement="right"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                  {
                    marginTop: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                  {
                    marginBottom: "0.2vw",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                  {
                    marginLeft: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                  {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.7vw",
                  padding: "0.5vw 0.5vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  fontWeight: "bold",
                  fontFamily: "Roboto-Medium",
                  color: "#000000",
                  marginBottom: "-5px",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(255,255,255,0.9)",
                },
              },
            }}
          >
            <IconButton
              sx={{
                backgroundColor: "#ffffff",
                color: "#18234f",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "4vw",
                height: "4vw",
              }}
              onClick={reloadPage}
            >
              <AddIcon sx={{ fontSize: "1.8vw" }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={"Buscar operacion"}
            arrow
            placement="right"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                  {
                    marginTop: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                  {
                    marginBottom: "0.2vw",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                  {
                    marginLeft: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                  {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.7vw",
                  padding: "0.5vw 0.5vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  fontWeight: "bold",
                  fontFamily: "Roboto-Medium",
                  color: "#000000",
                  marginBottom: "-5px",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(255,255,255,0.9)",
                },
              },
            }}
          >
            <IconButton
              sx={{
                backgroundColor: "#ffffff",
                color: "#18234f",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "4vw",
                height: "4vw",
              }}
              onClick={mostrarBuscarEgresosIngresos}
            >
              <SearchIcon sx={{ fontSize: "1.8vw" }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={"Imprimir operaciones"}
            arrow
            placement="right"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                  {
                    marginTop: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                  {
                    marginBottom: "0.2vw",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                  {
                    marginLeft: "0px",
                  },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                  {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.7vw",
                  padding: "0.5vw 0.5vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  fontWeight: "bold",
                  fontFamily: "Roboto-Medium",
                  color: "#000000",
                  marginBottom: "-5px",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(255,255,255,0.9)",
                },
              },
            }}
          >
            <IconButton
              sx={{
                backgroundColor: "#ffffff",
                color: "#18234f",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "4vw",
                height: "4vw",
              }}
            >
              <PrintIcon sx={{ fontSize: "1.8vw" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};
export default EgresosIngresos;
